import React, { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Box, Container, Card, Typography, Stack, AlertColor, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import {
  type ProgressListItem,
  PortfolioIntelligence,
  SalesMetricsCards,
  SalesCallsMade,
  CustomDateRangeSelector,
  IndividualPerformance,
  RoomNightAnalysis,
} from './components';
import axiosInstance from 'utils/axios';
import { privileges } from 'utils/constants/privileges';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import Dropdown from 'components/Dropdown';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import PageLoader from 'components/PageLoader';
import Notification from 'components/Notification';
import ViewCompanyProfile from '../SalesManagement/CompanyMaster/ViewCompanyProfile';
import { TabPanel } from 'utils/validators/HelperComponents';
import DrawerMenu from 'components/DrawerMenu';
import CallsMadeDialog from '../SalesManagement/SalesReport/DateWiseSummary/CallsMadeDialog';
import BusinessSecuredDialog from '../SalesManagement/SalesReport/DateWiseSummary/BusinessSecuredDialog';
import ScrollableTable from 'components/ScrollableTable';
import DrawerContent from './components/DrawerContent';
import StyledTabs from 'components/StyledTabs';
import strings from 'common/Translation/Translate';
import { pageSubtitleSx, pageTitleSxResponsive } from 'utils/styles/pageHeader';

import { RangePreset } from './types';
import { getRangePresetValue, formatRangeSubtitle } from './utils/rangePresetUtils';
import {
  createDateRenderer,
  createCompanyNameRenderer,
  createStatusRenderer,
  createDescriptionRenderer,
  createNumericRenderer,
} from './utils/tableRenderers';

const RANGE_PRESETS = [
  { id: 'TODAY', name: 'Today' },
  { id: 'THIS_WEEK', name: 'This Week' },
  { id: 'THIS_MONTH', name: 'This Month' },
  { id: 'CUSTOM', name: 'Custom Date' },
];

const getPresetDates = (preset: RangePreset) => {
  const today = dayjs();
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;

  switch (preset) {
    case 'TODAY':
      start = today.startOf('day');
      end = today.endOf('day');
      break;
    case 'THIS_WEEK':
      start = today.startOf('week');
      end = today.endOf('week');
      break;
    case 'THIS_MONTH':
    default:
      start = today.startOf('month');
      end = today.endOf('month');
      break;
  }

  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
};

const SalesReport: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userPrivileges = useSelector(Privilege);
  const canAccess = userPrivileges?.includes(privileges.view_sales_report);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [rangePreset, setRangePreset] = useState<RangePreset>('THIS_WEEK');
  const [dateRange, setDateRange] = useState(() => getPresetDates('THIS_WEEK'));
  const [customDateDrawerOpen, setCustomDateDrawerOpen] = useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const today = dayjs();
    return {
      startDate: today.format('YYYY-MM-DD'),
      endDate: today.format('YYYY-MM-DD'),
    };
  });
  const [selectedManager, setSelectedManager] = useState<string | number>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string | number>('');
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>('YEARLY');
  const [portfolioExecutive, setPortfolioExecutive] = useState<string | number>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [activitySummary, setActivitySummary] = useState<any>(null);
  const [activityLoading, setActivityLoading] = useState<boolean>(false);
  const [managerWisePerformance, setManagerWisePerformance] = useState<any[]>([]);
  const [managerWisePerformanceLoading, setManagerWisePerformanceLoading] = useState<boolean>(false);
  const [individualPerformance, setIndividualPerformance] = useState<any>(null);
  const [individualPerformanceLoading, setIndividualPerformanceLoading] = useState<boolean>(false);
  const [executivePortfolio, setExecutivePortfolio] = useState<any>(null);
  const [executivePortfolioLoading, setExecutivePortfolioLoading] = useState<boolean>(false);
  const [companyPortfolio, setCompanyPortfolio] = useState<any>(null);
  const [companyPortfolioLoading, setCompanyPortfolioLoading] = useState<boolean>(false);
  const [roomNightAnalysis, setRoomNightAnalysis] = useState<any>(null);
  const [roomNightAnalysisLoading, setRoomNightAnalysisLoading] = useState<boolean>(false);
  const [periods, setPeriods] = useState<any[]>([]);
  const [managerWisePeriods, setManagerWisePeriods] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [openCompanyProfile, setOpenCompanyProfile] = useState<boolean>(false);
  const [callHistoryDrawerOpen, setCallHistoryDrawerOpen] = useState<boolean>(false);
  const [selectedCalls, setSelectedCalls] = useState<any[]>([]);
  const [businessSecuredDrawerOpen, setBusinessSecuredDrawerOpen] = useState<boolean>(false);
  const [followUpsDrawerOpen, setFollowUpsDrawerOpen] = useState<boolean>(false);
  const [selectedFollowUps, setSelectedFollowUps] = useState<any[]>([]);
  const [selectedBookingLineItems, setSelectedBookingLineItems] = useState<any[]>([]);
  const [categoryCompaniesDrawerOpen, setCategoryCompaniesDrawerOpen] = useState<boolean>(false);
  const [selectedCategoryCompanies, setSelectedCategoryCompanies] = useState<any[]>([]);
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState<string>('');
  const [selectedManagerName, setSelectedManagerName] = useState<string>('');
  const [managerWisePeriod, setManagerWisePeriod] = useState<string | number>('');
  const [managerWisePeriodType, setManagerWisePeriodType] = useState<string>('YEARLY');
  const [filterOptions, setFilterOptions] = useState<{
    managers: any[];
    categories: any[];
  }>({
    managers: [],
    categories: [],
  });
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleError = (err: any) => {
    if (err.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: err.response?.data.message || 'An error occurred',
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: err.message || 'An error occurred',
      });
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage(prev => ({ ...prev, display: value }));
  };

  // Fetch filter dropdowns
  const fetchFilterDropdowns = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getCompanyFilters);
      const managers = data?.primaryManagers || [];
      const categories = data?.companyCategories || [];
      setFilterOptions({ managers, categories });

      // Auto-select if only one manager
      if (managers.length === 1) {
        setSelectedManager(managers[0].id);
        setPortfolioExecutive(managers[0].id);
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch sales overview
  const fetchSalesOverview = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.post(URLS.getSalesOverview);
      setOverviewData(data);
    } catch (err: any) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch activity summary
  const fetchActivitySummary = useCallback(
    async (userId: string | number, range: { startDate: string; endDate: string }) => {
      if (!userId) {
        setActivitySummary(null);
        return;
      }
      try {
        setActivityLoading(true);
        const { data } = await axiosInstance.get(URLS.getDateWiseSummary(String(userId), range));
        setActivitySummary(data);
      } catch (err: any) {
        handleError(err);
      } finally {
        setActivityLoading(false);
      }
    },
    [],
  );

  // Fetch executive portfolio
  const fetchExecutivePortfolio = useCallback(async (userId: string | number) => {
    if (!userId) {
      setExecutivePortfolio(null);
      return;
    }
    try {
      setExecutivePortfolioLoading(true);
      const { data } = await axiosInstance.get(URLS.getExecutivePortfolio(String(userId)));
      setExecutivePortfolio(data);
    } catch (err: any) {
      handleError(err);
    } finally {
      setExecutivePortfolioLoading(false);
    }
  }, []);

  // Fetch company portfolio for all categories
  const fetchAllCompanyPortfolio = useCallback(async () => {
    try {
      setCompanyPortfolioLoading(true);
      const categories: Array<'A' | 'B' | 'C' | 'O'> = ['A', 'B', 'C', 'O'];
      const responses = await Promise.all(
        categories.map(cat => axiosInstance.get(URLS.getCompanyPortfolio(cat))),
      );
      const merged = responses.flatMap((res, index) => {
        const list = res.data?.categorySummaries ?? [];
        return list.map((company: any) => ({
          ...company,
          category: company.category ?? categories[index],
        }));
      });
      setCompanyPortfolio(merged);
    } catch (err: any) {
      handleError(err);
    } finally {
      setCompanyPortfolioLoading(false);
    }
  }, []);

  // Fetch periods dropdown
  const fetchPeriods = useCallback(async (periodType: string = 'YEARLY') => {
    try {
      const { data } = await axiosInstance.get(URLS.getTargetPeriodsDropdown(periodType));
      if (data && data.length > 0) {
        const periodsData = createDropdownData(data, ['id', 'name']);
        setPeriods(periodsData);
        // Set first period as selected
        if (periodsData.length > 0 && periodsData[0].value) {
          setSelectedPeriod(periodsData[0].value);
        }
      } else {
        setPeriods([]);
        setSelectedPeriod('');
      }
    } catch (err: any) {
      handleError(err);
      setPeriods([]);
      setSelectedPeriod('');
    }
  }, []);

  // Fetch manager-wise periods dropdown
  const fetchManagerWisePeriods = useCallback(async (periodType: string = 'YEARLY') => {
    try {
      const { data } = await axiosInstance.get(URLS.getTargetPeriodsDropdown(periodType));
      if (data && data.length > 0) {
        const periodsData = createDropdownData(data, ['id', 'name']);
        setManagerWisePeriods(periodsData);
        if (periodsData.length > 0 && periodsData[0].value) {
          setManagerWisePeriod(periodsData[0].value);
        }
      } else {
        setManagerWisePeriods([]);
        setManagerWisePeriod('');
      }
    } catch (err: any) {
      handleError(err);
      setManagerWisePeriods([]);
      setManagerWisePeriod('');
    }
  }, []);

  // Fetch individual performance
  const fetchIndividualPerformance = useCallback(async (userId: string | number, period: string | number) => {
    if (!userId || !period) {
      setIndividualPerformance(null);
      return;
    }
    try {
      setIndividualPerformanceLoading(true);
      const periodId = Number(period);
      if (isNaN(periodId)) {
        setIndividualPerformance(null);
        return;
      }
      const { data } = await axiosInstance.get(URLS.getIndividualPerformance(Number(userId), periodId));
      // Ensure we have valid data structure
      if (data) {
        setIndividualPerformance({
          roomNight: data.roomNight || { target: 0, achieved: 0, variance: 0, percentage: 0 },
          roomRevenue: data.roomRevenue || { target: 0, achieved: 0, variance: 0, percentage: 0 },
          miceCount: data.miceCount || { target: 0, achieved: 0, variance: 0, percentage: 0 },
          miceRevenue: data.miceRevenue || { target: 0, achieved: 0, variance: 0, percentage: 0 },
        });
      } else {
        setIndividualPerformance(null);
      }
    } catch (err: any) {
      handleError(err);
      setIndividualPerformance(null);
    } finally {
      setIndividualPerformanceLoading(false);
    }
  }, []);

  // Fetch manager-wise performance
  const fetchManagerWisePerformance = useCallback(async (period: string | number) => {
    if (!period) {
      setManagerWisePerformance([]);
      return;
    }
    try {
      setManagerWisePerformanceLoading(true);
      const periodId = Number(period);
      if (isNaN(periodId)) {
        setManagerWisePerformance([]);
        return;
      }
      const { data } = await axiosInstance.get(URLS.getManagerWisePerformance(periodId));
      setManagerWisePerformance(Array.isArray(data) ? data : []);
    } catch (err: any) {
      handleError(err);
      setManagerWisePerformance([]);
    } finally {
      setManagerWisePerformanceLoading(false);
    }
  }, []);

  const fetchRoomNightAnalysis = useCallback(
    async (userId: string | number, range: { startDate: string; endDate: string }) => {
      try {
        setRoomNightAnalysisLoading(true);
        const { data } = await axiosInstance.get(URLS.getRoomNightAnalysis(range, userId || undefined));
        setRoomNightAnalysis(data);
      } catch (err: any) {
        handleError(err);
        setRoomNightAnalysis(null);
      } finally {
        setRoomNightAnalysisLoading(false);
      }
    },
    [],
  );

  const handleCompanyClick = useCallback(async (id: number) => {
    try {
      const { data } = await axiosInstance.get(URLS.getCompanyProfileById(id));
      setSelectedCompany(data);
      setOpenCompanyProfile(true);
    } catch (error: any) {
      handleError(error);
    }
  }, []);

  const handleCloseCompanyProfile = () => {
    setOpenCompanyProfile(false);
    setSelectedCompany(null);
  };

  const handleRangeChange = (preset: RangePreset) => {
    if (preset === 'CUSTOM') {
      // Always open drawer when CUSTOM is selected, even if already selected
      if (rangePreset === 'CUSTOM') {
        setCustomDateRange(dateRange);
      }
      // Use setTimeout to ensure drawer opens even if dropdown doesn't trigger onChange for same value
      setTimeout(() => {
        setCustomDateDrawerOpen(true);
      }, 0);
    } else {
      setRangePreset(preset);
      setDateRange(getPresetDates(preset));
    }
  };

  // Handle case when CUSTOM is already selected and user clicks on it again
  const handleCustomDateClick = () => {
    if (rangePreset === 'CUSTOM') {
      setCustomDateRange(dateRange);
      setCustomDateDrawerOpen(true);
    }
  };

  const handleCustomDateApply = () => {
    if (dayjs(customDateRange.endDate).isBefore(dayjs(customDateRange.startDate))) {
      setMessage({
        display: true,
        severity: 'warning',
        message: 'End date must be greater than or equal to start date',
      });
      return;
    }
    setRangePreset('CUSTOM');
    setDateRange(customDateRange);
    setCustomDateDrawerOpen(false);
  };

  const handleQuickDateSelect = (preset: 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH') => {
    const dates = getPresetDates(preset);
    setCustomDateRange(dates);
    setRangePreset(preset);
    setDateRange(dates);
    setCustomDateDrawerOpen(false);
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!canAccess) return;
    fetchFilterDropdowns();
    fetchSalesOverview();
  }, [canAccess, fetchFilterDropdowns, fetchSalesOverview]);

  useEffect(() => {
    if (!canAccess || !selectedManager) return;
    fetchActivitySummary(selectedManager, dateRange);
  }, [canAccess, selectedManager, dateRange.startDate, dateRange.endDate, fetchActivitySummary, dateRange]);

  useEffect(() => {
    if (!canAccess || !portfolioExecutive) return;
    fetchExecutivePortfolio(portfolioExecutive);
  }, [canAccess, portfolioExecutive, fetchExecutivePortfolio]);

  useEffect(() => {
    if (!canAccess || activeTab !== 1) return;
    fetchAllCompanyPortfolio();
  }, [canAccess, activeTab, fetchAllCompanyPortfolio]);

  useEffect(() => {
    if (!canAccess) return;
    fetchPeriods(selectedPeriodType);
  }, [canAccess, selectedPeriodType, fetchPeriods]);

  useEffect(() => {
    if (!canAccess) return;
    if (selectedManager && selectedPeriod) {
      fetchIndividualPerformance(selectedManager, selectedPeriod);
    } else {
      setIndividualPerformance(null);
    }
  }, [canAccess, selectedManager, selectedPeriod, fetchIndividualPerformance]);

  useEffect(() => {
    if (!canAccess) return;
    fetchManagerWisePeriods(managerWisePeriodType);
  }, [canAccess, managerWisePeriodType, fetchManagerWisePeriods]);

  useEffect(() => {
    if (!canAccess) return;
    if (managerWisePeriod) {
      fetchManagerWisePerformance(managerWisePeriod);
    } else {
      setManagerWisePerformance([]);
    }
  }, [canAccess, managerWisePeriod, fetchManagerWisePerformance]);

  useEffect(() => {
    if (!canAccess || activeTab !== 2) return;
    fetchRoomNightAnalysis(selectedManager, dateRange);
  }, [canAccess, activeTab, selectedManager, dateRange, fetchRoomNightAnalysis]);

  // Map manager performance items
  const managerPerformanceItems: ProgressListItem[] = useMemo(() => {
    const list = overviewData?.managerWiseCalls ?? [];
    const palette = ['#0EA5E9', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'];
    return list.map((row: any, index: number) => {
      const values = {
        today: Number(row?.today ?? 0),
        week: Number(row?.avgWeek ?? 0),
        month: Number(row?.avgMonth ?? 0),
      };

      const { value, displayValue } = getRangePresetValue(rangePreset, values);

      return {
        title: row?.managerName ?? 'Manager',
        value: value,
        subtitle: formatRangeSubtitle(values),
        meta: undefined,
        accentClassName: palette[index % palette.length],
        customValue: displayValue,
      };
    });
  }, [overviewData, rangePreset]);

  // Map company engagement items
  const companyEngagementItems: ProgressListItem[] = useMemo(() => {
    const list = activitySummary?.companyWiseCalls ?? [];
    const palette = ['#6366F1', '#EF4444', '#14B8A6', '#F97316', '#8B5CF6', '#EC4899'];
    return list.map((row: any, index: number) => {
      const values = {
        today: Number(row?.today ?? 0),
        week: Number(row?.avgWeek ?? 0),
        month: Number(row?.avgMonth ?? 0),
      };

      const { value, displayValue } = getRangePresetValue(rangePreset, values);

      return {
        title: row?.companyName ?? 'Company',
        value: value,
        subtitle: formatRangeSubtitle(values),
        meta: row?.managerName ? `Manager: ${row.managerName}` : undefined,
        accentClassName: palette[index % palette.length],
        onClick: row?.accountId ? () => handleCompanyClick(Number(row.accountId)) : undefined,
        customValue: displayValue,
      };
    });
  }, [activitySummary, handleCompanyClick, rangePreset]);

  // Extract company portfolio
  const extractedCompanyPortfolio = useMemo(() => {
    const categoryOrder: Record<string, number> = { A: 0, B: 1, C: 2, O: 3 };
    let list: any[] = [];

    if (Array.isArray(companyPortfolio)) {
      list = companyPortfolio;
    } else if (companyPortfolio) {
      if (Array.isArray(companyPortfolio?.categorySummaries)) {
        list = companyPortfolio.categorySummaries;
      } else if (Array.isArray(companyPortfolio?.companies)) {
        list = companyPortfolio.companies;
      } else if (companyPortfolio?.categorySummaries && typeof companyPortfolio.categorySummaries === 'object') {
        list = Object.values(companyPortfolio.categorySummaries).flatMap((item: any) => item?.companies ?? []);
      }
    }

    return [...list]
      .sort((a, b) => {
        const catA = categoryOrder[String(a?.category ?? 'O').charAt(0).toUpperCase()] ?? 99;
        const catB = categoryOrder[String(b?.category ?? 'O').charAt(0).toUpperCase()] ?? 99;
        if (catA !== catB) return catA - catB;
        return String(a?.companyName ?? a?.name ?? '').localeCompare(String(b?.companyName ?? b?.name ?? ''));
      })
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }, [companyPortfolio]);

  const managerAccountSummary = overviewData?.managerAccountSummary ?? [];

  if (!canAccess) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <Box sx={{ width: '100%', px: 1, py: { xs: 1.5, sm: 2 } }}>
      <Container maxWidth={false} sx={{ px: 0 }}>
        {(isLoading ||
          activityLoading ||
          individualPerformanceLoading ||
          executivePortfolioLoading ||
          companyPortfolioLoading ||
          roomNightAnalysisLoading) && <PageLoader />}
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
            autoHideDuration={2000}
          />
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 1,
          }}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography sx={pageTitleSxResponsive(isMobile)}>{strings.sales_reports}</Typography>
            <Typography sx={{ ...pageSubtitleSx, mt: 0.25 }}>{strings.sales_reports_subtitle}</Typography>
          </Box>
          <StyledTabs
            compact
            tabs={[
              { label: 'Daily Sales Report' },
              { label: 'Portfolio Intelligence' },
              { label: 'Room Night Analysis' },
            ]}
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="sales report tabs"
          />
        </Box>

        {activeTab !== 1 ? (
          <Box
            sx={{
              width: '100%',
              px: 1.5,
              py: 1.25,
              mb: 1,
              borderRadius: '10px',
              bgcolor: '#F8FAFC',
              border: '1px solid',
              borderColor: '#EEF2F6',
            }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Dropdown
                label="Sales executive"
                value={selectedManager}
                options={filterOptions.managers.map((m: any) => ({
                  id: m.id,
                  name: m.name || m.label || String(m.id),
                }))}
                onChange={value => setSelectedManager(value)}
                minWidth={220}
                sx={{ ml: 0, flex: '1 1 0', minWidth: 0, bgcolor: '#fff' }}
              />
              <Dropdown
                label="Time Range"
                value={rangePreset}
                options={RANGE_PRESETS}
                onChange={value => handleRangeChange(value as RangePreset)}
                onItemClick={value => {
                  if (value === 'CUSTOM' && rangePreset === 'CUSTOM') {
                    handleCustomDateClick();
                  }
                }}
                minWidth={160}
                sx={{ ml: 0, flex: '1 1 0', minWidth: 0, bgcolor: '#fff' }}
              />
            </Stack>
          </Box>
        ) : null}

        {/* Daily Sales Report Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sales Activity Cards */}
            <SalesMetricsCards
              activitySummary={activitySummary}
              rangePreset={rangePreset}
              activityLoading={activityLoading}
              onCallsClick={() => {
                if (activitySummary?.salesCalls?.length) {
                  setSelectedCalls(activitySummary.salesCalls);
                  setCallHistoryDrawerOpen(true);
                }
              }}
              onFollowUpsClick={() => {
                if (activitySummary?.tasks?.length) {
                  setSelectedFollowUps(activitySummary.tasks);
                  setFollowUpsDrawerOpen(true);
                }
              }}
              onBusinessSecuredClick={() => {
                if (activitySummary?.bookingLineItems?.length) {
                  setSelectedBookingLineItems(activitySummary.bookingLineItems);
                  setBusinessSecuredDrawerOpen(true);
                }
              }}
            />

            {/* Individual Performance */}
            <IndividualPerformance
              performanceData={individualPerformance}
              loading={individualPerformanceLoading}
              selectedPeriod={selectedPeriod}
              selectedPeriodType={selectedPeriodType}
              periods={periods}
              onPeriodChange={setSelectedPeriod}
              onPeriodTypeChange={(periodType: string) => {
                setSelectedPeriodType(periodType);
              }}
              selectedManager={selectedManager}
            />

            {/* Sales Calls Made */}
            <SalesCallsMade
              managerPerformanceItems={managerPerformanceItems}
              companyEngagementItems={companyEngagementItems}
              isLoading={isLoading}
              activityLoading={activityLoading}
              selectedManager={selectedManager}
            />
          </Box>
        </TabPanel>

        {/* Portfolio Intelligence Tab */}
        <TabPanel value={activeTab} index={1}>
          <PortfolioIntelligence
            portfolioExecutive={portfolioExecutive}
            setPortfolioExecutive={setPortfolioExecutive}
            executivePortfolio={executivePortfolio}
            extractedCompanyPortfolio={extractedCompanyPortfolio}
            managerAccountSummary={managerAccountSummary}
            filterOptions={filterOptions}
            onCompanyClick={handleCompanyClick}
            onCategoryClick={(category, companies, managerName) => {
              setSelectedCategoryCompanies(companies);
              setSelectedCategoryLabel(`Category ${category}`);
              setSelectedManagerName(managerName);
              setCategoryCompaniesDrawerOpen(true);
            }}
            managerWisePerformance={managerWisePerformance}
            managerWisePerformanceLoading={managerWisePerformanceLoading}
            managerWisePeriodType={managerWisePeriodType}
            managerWisePeriod={managerWisePeriod}
            managerWisePeriods={managerWisePeriods}
            onManagerWisePeriodTypeChange={setManagerWisePeriodType}
            onManagerWisePeriodChange={setManagerWisePeriod}
          />
        </TabPanel>

        {/* Room Night Analysis Tab */}
        <TabPanel value={activeTab} index={2}>
          <RoomNightAnalysis
            data={roomNightAnalysis}
            loading={roomNightAnalysisLoading}
            selectedManager={selectedManager}
            managers={filterOptions.managers}
            rangePreset={rangePreset}
            rangeOptions={RANGE_PRESETS}
            onManagerChange={setSelectedManager}
            onRangeChange={handleRangeChange}
            onCustomRangeClick={value => {
              if (value === 'CUSTOM' && rangePreset === 'CUSTOM') {
                handleCustomDateClick();
              }
            }}
          />
        </TabPanel>
      </Container>

      {/* Company Profile Drawer */}
      {selectedCompany && (
        <ViewCompanyProfile
          data={selectedCompany}
          handleClose={handleCloseCompanyProfile}
          openPdfDrawer={openCompanyProfile}
        />
      )}

      {/* Call History Drawer */}
      <DrawerMenu
        width="100%"
        open={callHistoryDrawerOpen}
        handleClose={() => setCallHistoryDrawerOpen(false)}
        closeButton={true}
        title="Call History"
        dropBack={true}>
        <DrawerContent padding={2}>
          <CallsMadeDialog salesCallsMade={selectedCalls} />
        </DrawerContent>
      </DrawerMenu>

      {/* Business Secured Drawer */}
      <DrawerMenu
        width="70%"
        open={businessSecuredDrawerOpen}
        handleClose={() => setBusinessSecuredDrawerOpen(false)}
        closeButton={true}
        title="Business Secured"
        dropBack={true}>
        <DrawerContent padding={4} fullHeight={false}>
          <BusinessSecuredDialog data={selectedBookingLineItems} />
        </DrawerContent>
      </DrawerMenu>

      {/* Follow-ups Drawer */}
      <DrawerMenu
        width="70%"
        open={followUpsDrawerOpen}
        handleClose={() => setFollowUpsDrawerOpen(false)}
        closeButton={true}
        title="Follow-ups"
        dropBack={true}>
        <DrawerContent padding={4}>
          <ScrollableTable
            columns={[
              { label: 'Date', key: 'createdAt', align: 'left' },
              { label: 'Title', key: 'title', align: 'left' },
              { label: 'Description', key: 'description', align: 'left' },
              { label: 'Status', key: 'status', align: 'left' },
              { label: 'Due Date', key: 'dueDate', align: 'left' },
            ]}
            data={selectedFollowUps}
            renderCell={(column, row) => {
              const cellRenderers: Record<string, (column: any, row: any) => React.ReactNode> = {
                createdAt: createDateRenderer('createdAt'),
                dueDate: createDateRenderer('dueDate'),
                description: createDescriptionRenderer(),
                status: createStatusRenderer(),
              };
              const renderer = column.key ? cellRenderers[column.key] : null;
              return renderer ? (
                renderer(column, row)
              ) : (
                <Typography variant="body2">{row[column.key || ''] ?? '-'}</Typography>
              );
            }}
            emptyMessage="No follow-ups available"
            maxHeight="100%"
          />
        </DrawerContent>
      </DrawerMenu>

      {/* Category Companies Drawer */}
      <DrawerMenu
        width="75%"
        open={categoryCompaniesDrawerOpen}
        handleClose={() => setCategoryCompaniesDrawerOpen(false)}
        closeButton={true}
        title={`${selectedManagerName} - Category ${selectedCategoryLabel} Companies`}
        dropBack={true}>
        <DrawerContent padding={4}>
          <ScrollableTable
            columns={[
              { label: 'Company Name', key: 'companyName', align: 'left' },
              { label: 'MICE Event Count', key: 'miceEventCount', align: 'center' },
              { label: 'MICE Event Potential', key: 'miceEventPotential', align: 'center' },
              { label: 'FIT Room Nights', key: 'fitRoomNights', align: 'center' },
              { label: 'FIT Room Potential', key: 'fitRoomPotential', align: 'center' },
            ]}
            data={selectedCategoryCompanies}
            maxHeight="100%"
            onRowClick={row => handleCompanyClick(Number(row?.id || row?.accountId))}
            renderCell={(column, row) => {
              const cellRenderers: Record<string, (column: any, row: any) => React.ReactNode> = {
                companyName: createCompanyNameRenderer(),
                miceEventCount: createNumericRenderer('miceEventCount'),
                miceEventPotential: createNumericRenderer('miceEventPotential'),
                fitRoomNights: createNumericRenderer('fitRoomNights'),
                fitRoomPotential: createNumericRenderer('fitRoomPotential'),
              };
              const renderer = column.key ? cellRenderers[column.key] : null;
              return renderer ? renderer(column, row) : <Typography variant="body2">—</Typography>;
            }}
            getRowKey={(row, index) => row?.id || row?.accountId || index}
            emptyMessage="No companies found"
          />
        </DrawerContent>
      </DrawerMenu>

      {/* Custom Date Range Drawer */}
      <DrawerMenu
        width={500}
        open={customDateDrawerOpen}
        handleClose={() => setCustomDateDrawerOpen(false)}
        closeButton={true}
        title="Select Date Range"
        dropBack={true}>
        <DrawerContent padding={4}>
          <CustomDateRangeSelector
            rangePreset={rangePreset}
            customDateRange={customDateRange}
            onDateChange={handleCustomDateChange}
            onQuickSelect={handleQuickDateSelect}
            onApply={handleCustomDateApply}
            onCancel={() => setCustomDateDrawerOpen(false)}
          />
        </DrawerContent>
      </DrawerMenu>
    </Box>
  );
};

export default SalesReport;
